/**
 * Undo/Redo feature for @toolbox-web/grid-vue
 *
 * Import this module to enable the `undoRedo` prop on TbwGrid.
 * Also exports `useGridUndoRedo()` composable for programmatic undo/redo control.
 *
 * @example
 * ```vue
 * <script setup>
 * import '@toolbox-web/grid-vue/features/undo-redo';
 * </script>
 *
 * <template>
 *   <TbwGrid editing="dblclick" undoRedo />
 * </template>
 * ```
 *
 * @example Using the composable
 * ```vue
 * <script setup>
 * import { useGridUndoRedo } from '@toolbox-web/grid-vue/features/undo-redo';
 *
 * const { undo, redo, canUndo, canRedo } = useGridUndoRedo();
 * </script>
 *
 * <template>
 *   <button @click="undo" :disabled="!canUndo()">Undo</button>
 *   <button @click="redo" :disabled="!canRedo()">Redo</button>
 * </template>
 * ```
 *
 * @packageDocumentation
 */

import type { DataGridElement } from '@toolbox-web/grid';
import { type UndoRedoAction, type UndoRedoPlugin } from '@toolbox-web/grid/plugins/undo-redo';
import { inject, onBeforeUnmount, onMounted, ref, type Ref } from 'vue';
import { GRID_ELEMENT_KEY } from '../lib/use-grid';
import { useGridIsReady } from '../lib/use-grid-is-ready';

// Delegate to core feature registration
import '@toolbox-web/grid/features/undo-redo';
// Named type re-export surfaces the core `FeatureConfig` augmentation to dist
// consumers — a bare side-effect import alone is stripped from the emitted
// `.d.ts`. See `.github/knowledge/adapters.md`.
export type { _Augmentation as _UndoRedoAugmentation } from '@toolbox-web/grid/features/undo-redo';

/**
 * Undo/Redo methods returned from useGridUndoRedo.
 */
export interface UndoRedoMethods {
  /**
   * Undo the last edit action.
   * @returns The undone action (or compound action), or null if nothing to undo
   */
  undo: () => UndoRedoAction | null;

  /**
   * Redo the last undone action.
   * @returns The redone action (or compound action), or null if nothing to redo
   */
  redo: () => UndoRedoAction | null;

  /**
   * Check if there are any actions that can be undone.
   */
  canUndo: () => boolean;

  /**
   * Check if there are any actions that can be redone.
   */
  canRedo: () => boolean;

  /**
   * Clear all undo/redo history.
   */
  clearHistory: () => void;

  /**
   * Get a copy of the current undo stack.
   */
  getUndoStack: () => UndoRedoAction[];

  /**
   * Get a copy of the current redo stack.
   */
  getRedoStack: () => UndoRedoAction[];

  /**
   * Manually record an edit action.
   * If a transaction is active, the action is buffered; otherwise it's pushed to the undo stack.
   */
  recordEdit: (rowIndex: number, field: string, oldValue: unknown, newValue: unknown) => void;

  /**
   * Begin a transaction. All edits recorded until `endTransaction()` are grouped
   * into a single compound action for undo/redo.
   * @throws If a transaction is already active
   */
  beginTransaction: () => void;

  /**
   * End the active transaction and push the compound action to the undo stack.
   * If only one edit was recorded, it's pushed as a plain EditAction.
   * If no edits were recorded, the transaction is discarded.
   * @throws If no transaction is active
   */
  endTransaction: () => void;

  /**
   * Whether the grid has finished its first render.
   *
   * @since 2.5.0
   */
  isReady: Ref<boolean>;
}

/**
 * Composable for programmatic undo/redo control.
 *
 * Must be used within a component that contains a TbwGrid with undoRedo and editing enabled.
 *
 * @example
 * ```vue
 * <script setup>
 * import { useGridUndoRedo } from '@toolbox-web/grid-vue/features/undo-redo';
 *
 * const { undo, redo, canUndo, canRedo, clearHistory } = useGridUndoRedo();
 * </script>
 *
 * <template>
 *   <div class="toolbar">
 *     <button @click="undo" :disabled="!canUndo()">Undo</button>
 *     <button @click="redo" :disabled="!canRedo()">Redo</button>
 *     <button @click="clearHistory">Clear History</button>
 *   </div>
 * </template>
 * ```
 * @param selector - Optional CSS selector to target a specific grid element via
 *   DOM query instead of using Vue's provide/inject. Use when the component
 *   contains multiple grids, e.g. `'tbw-grid.primary'` or `'#my-grid'`.
 */
export function useGridUndoRedo(selector?: string): UndoRedoMethods {
  const gridElement = selector ? ref(null) : inject(GRID_ELEMENT_KEY, ref(null));

  const getGrid = (): DataGridElement | null =>
    (selector ? document.querySelector(selector) : gridElement.value) as DataGridElement | null;

  const getPlugin = (): UndoRedoPlugin | undefined => {
    return getGrid()?.getPluginByName('undoRedo');
  };

  const isReady = useGridIsReady(getGrid);

  // Re-evaluate `canUndo()` / `canRedo()` in templates whenever the undo/redo
  // history changes, so they behave like the Angular adapter's signals.
  const historyVersion = ref(0);
  let unsubHistory: (() => void) | undefined;

  onMounted(() => {
    const grid = getGrid();
    if (!grid?.on) return;

    const bump = () => historyVersion.value++;
    const unsubs = [grid.on('undo', bump), grid.on('redo', bump), grid.on('cell-commit', bump)];
    unsubHistory = () => unsubs.forEach((fn) => fn());
  });

  onBeforeUnmount(() => {
    unsubHistory?.();
    unsubHistory = undefined;
  });

  return {
    isReady,
    undo: () => {
      const plugin = getPlugin();
      if (!plugin) {
        console.warn(
          `[tbw-grid:undoRedo] UndoRedoPlugin not found.\n\n` +
            `  → Enable undo/redo on the grid:\n` +
            `    <TbwGrid editing="dblclick" undoRedo />`,
        );
        return null;
      }
      return plugin.undo();
    },

    redo: () => {
      const plugin = getPlugin();
      if (!plugin) {
        console.warn(
          `[tbw-grid:undoRedo] UndoRedoPlugin not found.\n\n` +
            `  → Enable undo/redo on the grid:\n` +
            `    <TbwGrid editing="dblclick" undoRedo />`,
        );
        return null;
      }
      return plugin.redo();
    },

    canUndo: () => {
      // Touch the version ref so Vue tracks this call as a reactive dependency.
      void historyVersion.value;
      return getPlugin()?.canUndo() ?? false;
    },

    canRedo: () => {
      void historyVersion.value;
      return getPlugin()?.canRedo() ?? false;
    },

    clearHistory: () => {
      const plugin = getPlugin();
      if (!plugin) {
        console.warn(
          `[tbw-grid:undoRedo] UndoRedoPlugin not found.\n\n` +
            `  → Enable undo/redo on the grid:\n` +
            `    <TbwGrid editing="dblclick" undoRedo />`,
        );
        return;
      }
      plugin.clearHistory();
    },

    getUndoStack: () => getPlugin()?.getUndoStack() ?? [],

    getRedoStack: () => getPlugin()?.getRedoStack() ?? [],

    recordEdit: (rowIndex: number, field: string, oldValue: unknown, newValue: unknown) => {
      const plugin = getPlugin();
      if (!plugin) {
        console.warn(
          `[tbw-grid:undoRedo] UndoRedoPlugin not found.\n\n` +
            `  → Enable undo/redo on the grid:\n` +
            `    <TbwGrid editing="dblclick" undoRedo />`,
        );
        return;
      }
      plugin.recordEdit(rowIndex, field, oldValue, newValue);
    },

    beginTransaction: () => {
      const plugin = getPlugin();
      if (!plugin) {
        console.warn(
          `[tbw-grid:undoRedo] UndoRedoPlugin not found.\n\n` +
            `  → Enable undo/redo on the grid:\n` +
            `    <TbwGrid editing="dblclick" undoRedo />`,
        );
        return;
      }
      plugin.beginTransaction();
    },

    endTransaction: () => {
      const plugin = getPlugin();
      if (!plugin) {
        console.warn(
          `[tbw-grid:undoRedo] UndoRedoPlugin not found.\n\n` +
            `  → Enable undo/redo on the grid:\n` +
            `    <TbwGrid editing="dblclick" undoRedo />`,
        );
        return;
      }
      plugin.endTransaction();
    },
  };
}
